import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import NavigationBar from '../components/NavigationBar';
import NowOverlay from '../components/NowOverlay';
import 'leaflet/dist/leaflet.css';
import './Map.css';
import { getGalleries, getNowEntries, filterNowEntries, NOW_PLACEMENT_MAP } from '../api/api';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { icons } from '../assets/icons';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: icons.starRetina, // Using optimized local import
  iconUrl: icons.star,
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const MapLocation = () => {
  const { type } = useParams();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Cache for custom icons to avoid recreation
  const iconCache = useMemo(() => new Map(), []);

  // NOW-Beitraege mit Anzeigeort "Karte": einmal pro Browser-Sitzung
  // automatisch mit dem neuesten Beitrag als Overlay oeffnen (Vor/Zurueck-
  // Navigation zu aelteren Beitraegen erledigt die NowOverlay-Komponente selbst).
  // Die Beitraege der Startseite zeigt Home.js.
  const [nowEntries, setNowEntries] = useState([]);
  const [showNowOverlay, setShowNowOverlay] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('now_overlay_shown')) return;

    getNowEntries().then(data => {
      const list = filterNowEntries(data?.data, NOW_PLACEMENT_MAP);
      if (list.length > 0) {
        setNowEntries(list);
        setShowNowOverlay(true);
      }
      sessionStorage.setItem('now_overlay_shown', '1');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await getGalleries();
        
        // Check if there are any locations for the selected type
        const locationsForType = type ? data.filter(location => location.Type === type) : [];
        
        // If no locations found for the type, use all locations
        const filteredData = locationsForType.length > 0 ? locationsForType : data;

        // Process locations directly without GeoJSON conversion
        const processedLocations = filteredData.map(location => {
          const coordinates = location.Coordinate.split(',').map(coord => parseFloat(coord.trim()));
          return {
            id: location.documentId,
            name: location.Title,
            description: location.Description,
            dates: location.Schedule,
            location: location.Location,
            website: location.Website,
            mapIcon: location.MapIcon,
            type: location.Type,
            coordinates: [coordinates[0], coordinates[1]] // [lat, lng]
          };
        });
        
        setLocations(processedLocations);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError('Failed to load location data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [type]);

  const createCustomClusterIcon = (cluster) => {
    const count = cluster.getChildCount();
    
    // Determine size and color based on the number of points
    let size, bgColor;
    
    if (count < 10) {
      size = 30;
      bgColor = '#1978c8';
    } else if (count < 25) {
      size = 35;
      bgColor = '#ff6600';
    } else {
      size = 40;
      bgColor = '#ff0000';
    }
    
    return L.divIcon({
      html: `<div style="
        background-color: ${bgColor};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        border: 3px solid white;
        box-shadow: 0 0 5px rgba(0,0,0,0.3);
      ">${count}</div>`,
      className: "custom-cluster-icon",
      iconSize: L.point(size, size)
    });
  };

  const createPopupContent = (location) => {
    return `
      <div class="popup-content">
        <div class="popup-text">
          <h5 class="navigate-text"><strong>${location.name}</strong></h5>
          <p class="navigate-text">${location.location}</p>
        </div>
        <div class="popup-icon">
          <img src="${icons.next}" alt="Icon" />
        </div>
      </div>
    `;
  };

  // Get default icon based on location type
  const getDefaultIconByType = (locationType) => {
    const typeIconMap = {
      'Art': icons.art,
      'Film & Theater': icons.film,
      'Architecture': icons.architecture,
      'Literature': icons.literature,
      'History': icons.history
    };
    
    return typeIconMap[locationType] || icons.star;
  };

  // Optimized icon creation with caching and thumbnail preference
  const createCustomIcon = useMemo(() => (location) => {
    const iconKey = location.mapIcon?.formats?.thumbnail?.url || 
                   location.mapIcon?.url || 
                   `default-${location.type}`;
    
    // Return cached icon if available
    if (iconCache.has(iconKey)) {
      return iconCache.get(iconKey);
    }
    
    let iconUrl;
    
    // Priority: thumbnail > small > original > default by type
    if (location.mapIcon?.formats?.thumbnail?.url) {
      iconUrl = `${process.env.REACT_APP_STRAPI_API_URL}${location.mapIcon.formats.thumbnail.url}`;
    } else if (location.mapIcon?.formats?.small?.url) {
      iconUrl = `${process.env.REACT_APP_STRAPI_API_URL}${location.mapIcon.formats.small.url}`;
    } else if (location.mapIcon?.url) {
      iconUrl = `${process.env.REACT_APP_STRAPI_API_URL}${location.mapIcon.url}`;
    } else {
      // Use default icon based on location type
      iconUrl = getDefaultIconByType(location.type);
    }
    
    const customIcon = new L.Icon({
      iconUrl: iconUrl,
      shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });
    
    // Cache the icon
    iconCache.set(iconKey, customIcon);
    return customIcon;
  }, [iconCache]);

  const nowOverlay = (
    <NowOverlay
      entries={showNowOverlay ? nowEntries : []}
      onClose={() => setShowNowOverlay(false)}
    />
  );

  if (loading) return (
    <>
      {nowOverlay}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    </>
  );

  if (error) return <>{nowOverlay}<p>{error}</p></>;
  if (!locations || locations.length === 0) return <>{nowOverlay}<p>No data available</p></>;

  return (
    <>
      {nowOverlay}
      <NavigationBar />
      <MapContainer 
        center={[52.526716, 13.411683]} 
        zoom={18} 
        style={{ height: "100vh", width: "100%" }} 
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        <MarkerClusterGroup 
          chunkedLoading
          iconCreateFunction={createCustomClusterIcon}
          spiderfyOnMaxZoom={true}
          disableClusteringAtZoom={18}
          maxClusterRadius={50}
        >
          {locations.map((location) => {
            // Use optimized icon creation
            const customIcon = createCustomIcon(location);
            
            return (
              <Marker 
                key={location.id || `marker-${Math.random()}`}
                position={[location.coordinates[0], location.coordinates[1]]}
                icon={customIcon}
                eventHandlers={{
                  click: (e) => {
                    // Unbind any existing popup to ensure fresh content and listeners
                    if (e.target.getPopup()) {
                      e.target.unbindPopup();
                    }
                    const popup = L.popup({closeButton: false})
                      .setContent(createPopupContent(location));
                    e.target.bindPopup(popup).openPopup();

                    // Track marker click
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: 'marker_click',
                      location_name: location.name,
                      location_type: location.type,
                    });

                    // Add event listener after popup is opened
                    setTimeout(() => {
                      const navigateIcons = document.querySelectorAll('.popup-icon');
                      navigateIcons.forEach(icon => {
                        icon.addEventListener('click', () => {
                          if (location.website && location.website.trim() !== '') {
                            // Track external link click
                            window.dataLayer = window.dataLayer || [];
                            window.dataLayer.push({
                              event: 'external_link_click',
                              location_name: location.name,
                              link_url: location.website,
                            });
                            window.open(location.website, '_blank');
                          }
                        });
                      });
                    }, 0);
                  }
                }}
              >
              </Marker>
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>
    </>
  );
};

export default MapLocation;